import { z } from 'zod';
/**
 * §6's operational dashboard.
 *
 * ⚠️ **EVERY FIGURE COMES FROM THE SUMMARY TABLES**, which §6 states outright:
 * *"served from summary tables refreshed by scheduled jobs at least every 5
 * minutes, not from queries over raw log tables"*. `log_entry` is what a
 * platform administrator is searching during an incident, and a dashboard
 * recomputing thirty days per page load would scan ninety partitions per viewer.
 *
 * ⚠️ **SO THE RESPONSE CARRIES ITS OWN FRESHNESS, and that is the most important
 * honesty property here.** A summary is by construction behind — and if the
 * rollup job has stopped, the dashboard FREEZES rather than erroring. Frozen, it
 * reads "0 errors in the last hour", which is a confident lie in the reassuring
 * direction. `refreshedAt` and `stale` are what stop it telling that lie, and
 * they are the same rule as *a quiet day is only quiet if the fleet was
 * reporting*.
 */
export const LOG_DASHBOARD_WINDOWS = ['1h', '6h', '24h', '7d', '30d'];
export const logDashboardWindowSchema = z.enum(LOG_DASHBOARD_WINDOWS);
export const logDashboardQuerySchema = z
    .object({ window: logDashboardWindowSchema.default('24h') })
    .strict();
/** One point on the errors-and-warnings trend. */
export const logDashboardPointSchema = z.object({
    bucket: z.string(),
    warnings: z.number(),
    errors: z.number(),
});
export const logDashboardCodeSchema = z.object({
    code: z.string(),
    entries: z.number(),
    /**
     * From the registry, or null for a code this build does not know.
     *
     * ⚠️ Null rather than a default area: a code the registry has not caught up
     * with is still a real failure and must appear, but counting it under
     * whichever area happens to be first would put it on a panel it has nothing
     * to do with. The `policyLabel` rule — visibly unresolved beats invented.
     */
    area: z.string().nullable(),
    owner: z.string().nullable(),
    /** Whether §7 may fire on it. Shown so a busy code that alerts nobody is visible as such. */
    alertable: z.boolean().nullable(),
});
export const logDashboardAreaSchema = z.object({
    area: z.string(),
    label: z.string(),
    warnings: z.number(),
    errors: z.number(),
    /**
     * Every code in the area, so a panel can link to EXACTLY what it counted.
     *
     * ⚠️ **SENT BY THE SERVER, because the web app may not read the error-code
     * registry.** `@oip/observability` is a Node logging library — a browser
     * bundle has no business importing it, and the compiler said so when the first
     * version tried. Which turned out for the better: the drill-through is the
     * only check on `area` (the one registry field CI cannot verify), so it has to
     * show the same set the figure counted, and a client computing its own list
     * from a copied vocabulary is how the two come to disagree.
     */
    codes: z.array(z.string()),
});
export const logDashboardServiceSchema = z.object({
    service: z.string(),
    warnings: z.number(),
    errors: z.number(),
});
export const logDashboardTenantSchema = z.object({
    tenantId: z.string(),
    /**
     * Resolved from the transactional database where it still exists.
     *
     * ⚠️ NULL when it does not — a tenant deleted since the line was written, or
     * an id from another environment. Rendering the raw id is the honest fallback;
     * omitting the row would hide errors that happened.
     */
    name: z.string().nullable(),
    warnings: z.number(),
    errors: z.number(),
});
export const logDashboardStorageSchema = z.object({
    totalBytes: z.number(),
    byTier: z.array(z.object({ tier: z.string(), bytes: z.number(), partitions: z.number() })),
    oldestPartition: z.string().nullable(),
    newestPartition: z.string().nullable(),
});
export const logDashboardWriterSchema = z.object({
    instance: z.string(),
    service: z.string(),
    env: z.string(),
    reportedAt: z.string(),
    /** Measured against the DATABASE clock, so two skewed services cannot disagree. */
    ageMs: z.number(),
    /** §7: "any service's writer heartbeat stops for more than 5 minutes". */
    stale: z.boolean(),
    queueDepth: z.number(),
    /** §6's ingestion lag: the age of the oldest queued line. */
    oldestQueuedMs: z.number(),
    written: z.number(),
    writeFailures: z.number(),
    dropped: z.object({
        debug: z.number(),
        info: z.number(),
        warn: z.number(),
        error: z.number(),
        security: z.number(),
    }),
});
export const logDashboardResponseSchema = z.object({
    /** The window the SERVER resolved, echoed rather than left to the client's memory. */
    from: z.string(),
    to: z.string(),
    /**
     * How current the figures are — the summary watermark, not `now`.
     *
     * ⚠️ NULL before the rollup has ever run, which is a different fact from "up
     * to date" and must not draw the same. A dashboard that cannot say when it was
     * last refreshed is one whose zeroes cannot be trusted.
     */
    refreshedAt: z.string().nullable(),
    /**
     * The summary is further behind than one refresh interval allows.
     *
     * ⚠️ The single most important field here. When the rollup stops the dashboard
     * does not error — it freezes, and reads "0 errors" in the reassuring
     * direction. This is what makes the page say so.
     */
    stale: z.boolean(),
    series: z.array(logDashboardPointSchema),
    /** Totals over the whole window, so the page does not re-add the series. */
    totals: z.object({ warnings: z.number(), errors: z.number() }),
    byArea: z.array(logDashboardAreaSchema),
    /**
     * Warn-and-worse lines whose code is in no area — including lines with NO code
     * at all, which is most of them.
     *
     * ⚠️ REPORTED rather than dropped. Without it the area panel silently accounts
     * for a fraction of the errors while reading as a breakdown, which is the
     * "percentages do not add up" complaint this product has already had once.
     */
    unattributed: z.number(),
    byCode: z.array(logDashboardCodeSchema),
    byService: z.array(logDashboardServiceSchema),
    byTenant: z.array(logDashboardTenantSchema),
    storage: logDashboardStorageSchema,
    writers: z.array(logDashboardWriterSchema),
});
//# sourceMappingURL=log-dashboard.js.map